import { useMemo, useCallback, useState } from 'react';
import { graphql } from 'react-relay';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import type { ApolloInteropMutableHelper } from '@confluence/apollo-relay-interop/entry-points/ApolloInteropMutableHelper.ts';
import { usePageSpaceKey } from '@confluence/page-context';
import type { MyVisitsData } from '@confluence/local-activity/entry-points/useMyVisits.ts';
import { useMyVisits } from '@confluence/local-activity/entry-points/useMyVisits.ts';
import {
	getEmojiPublishedProperty,
	getEmojiDraftProperty,
} from '@confluence/emoji-title/entry-points/transformer.ts';
import { getTitleAndEmoji } from '@confluence/emoji-title/entry-points/helpers.ts';

import type { SpaceViewsPage } from './SpaceViewsList/SpaceViewsList.tsx';
import type { SpaceViewsRecentlyViewedQuery as ActivityQueryData } from './__types__/RecentlyViewedQuery.ts';
import type { SpaceViewsPersistenceOption } from './__generated__/useSpaceViewsOptionsPersistenceQuery.graphql.ts';
import type {
	useSpaceViewsDataSpaceRootQuery$data,
	useSpaceViewsDataSpaceRootQuery$variables,
} from './__generated__/useSpaceViewsDataSpaceRootQuery.graphql.ts';
import type {
	useSpaceViewsDataContentQuery$data,
	useSpaceViewsDataContentQuery$variables,
} from './__generated__/useSpaceViewsDataContentQuery.graphql.ts';

const PAGE_SIZE = 100;
const FETCH_POLICY = 'cache-and-network';
const ALPHA_TITLE_SORT = 'title';
const LAST_MODIFIED_SORT = 'history.by.when desc';
const CONTENT_TREE_NAV_TYPE = 'contentTree';

export const useSpaceViewsDataSpaceRootQuery = graphql`
	query useSpaceViewsDataSpaceRootQuery($spaceKey: String!) {
		space(key: $spaceKey) {
			id
			homepage {
				id
			}
			operations {
				operation
				targetType
			}
		}
	}
`;

type useSpaceViewsDataContentQuery_content_nodes = NonNullable<
	NonNullable<useSpaceViewsDataContentQuery$data['content']>['nodes']
>[0];

const useSpaceViewsDataContentQuery = graphql`
	query useSpaceViewsDataContentQuery(
		$first: Int!
		$spaceKey: String!
		$token: String
		$sort: String!
		$navigationType: String
	) {
		content(
			first: $first
			spaceKey: $spaceKey
			orderby: $sort
			after: $token
			status: "current"
			navigationType: $navigationType
		) {
			nodes {
				id
				title
				type
				subType
				hasRestrictions
				hasInheritedRestrictions
				links {
					webui
					base
				}
				properties(keys: ["emoji-title-published", "emoji-title-draft"]) {
					nodes {
						id
						key
						value
						version {
							number
						}
					}
				}
			}
			pageInfo {
				hasNextPage
				endCursor
			}
		}
	}
`;

export const useSpaceViewsData = (sort: SpaceViewsPersistenceOption) => {
	const [stateSpaceKey] = usePageSpaceKey();
	// @ts-ignore FIXME: `stateSpaceKey` can be `undefined` here, and needs proper handling
	const spaceKey: string = stateSpaceKey;
	const [paginationError, setPaginationError] = useState<ApolloErrorInterop>();
	const [paginationLoading, setPaginationLoading] = useState(false);

	const isSearch = sort === 'TITLE_AZ' || sort === 'RECENTLY_MODIFIED';
	const isRecent = sort == 'RECENTLY_VIEWED';

	const {
		data: spaceRootData,
		loading: spaceRootLoading,
		error: spaceRootError,
		refetch: spaceRootRefetch,
	} = useQueryInterop<
		useSpaceViewsDataSpaceRootQuery$data,
		useSpaceViewsDataSpaceRootQuery$variables
	>(useSpaceViewsDataSpaceRootQuery, {
		variables: {
			spaceKey,
		},
		fetchPolicy: FETCH_POLICY,
	});

	const {
		data: searchData,
		loading: searchLoading,
		fetchMore: searchFetchMore,
		error: searchError,
		refetch: searchRefetch,
	} = useQueryInterop<useSpaceViewsDataContentQuery$data, useSpaceViewsDataContentQuery$variables>(
		useSpaceViewsDataContentQuery,
		{
			variables: {
				first: PAGE_SIZE,
				spaceKey,
				sort: sort === 'TITLE_AZ' ? ALPHA_TITLE_SORT : LAST_MODIFIED_SORT,
				navigationType: CONTENT_TREE_NAV_TYPE,
			},
			skip: !isSearch,
			fetchPolicy: FETCH_POLICY,
		},
	);

	const {
		data: recentData,
		loading: recentLoading,
		nextPage: recentFetchMore,
		error: recentError,
		refetch: recentRefetch,
	} = useMyVisits({
		spaceKey,
		pageSize: PAGE_SIZE,
		skip: !isRecent,
		fetchPolicy: FETCH_POLICY,
	});

	const spaceRootId = spaceRootData?.space?.homepage?.id || null;

	const data = useMemo(() => {
		const { pagesList, pageInfo } = getNormalizedData(searchData || recentData) || {};
		return {
			pagesList: (pagesList ?? []).filter((item) => item?.id !== spaceRootId),
			pageInfo,
		};
	}, [spaceRootId, searchData, recentData]);

	const refetch = isSearch
		? () => {
				void spaceRootRefetch();
				void searchRefetch();
			}
		: isRecent
			? () => {
					void spaceRootRefetch();
					void recentRefetch();
				}
			: undefined;

	const loading = searchLoading || recentLoading || paginationLoading || spaceRootLoading;
	const fetchMore = useCallback(() => {
		const { hasNextPage, endCursor } = data?.pageInfo || {};
		if (hasNextPage && endCursor) {
			if (!loading) {
				/**
				 * We maintain a separate pagination loading state because of the separate error state.
				 * Without this, the hook would return `loading: false` before the error state has been updated
				 */
				setPaginationLoading(true);

				if (isSearch) {
					searchFetchMore({
						variables: {
							token: endCursor,
						},
						updateQuery: (
							prevResult: ApolloInteropMutableHelper<useSpaceViewsDataContentQuery$data>,
							{ fetchMoreResult },
						) => {
							if (!fetchMoreResult) return prevResult;
							const prevNodes = prevResult?.content?.nodes || [];
							const results = {
								...fetchMoreResult,
							} as ApolloInteropMutableHelper<useSpaceViewsDataContentQuery$data>;
							if (results?.content) {
								if (results.content?.nodes) {
									results.content.nodes = prevNodes.concat(results.content.nodes);
								} else {
									results.content.nodes = prevNodes;
								}
							}
							return results;
						},
					})
						.catch((error) => {
							setPaginationError(error);
						})
						.finally(() => {
							setPaginationLoading(false);
						});
				} else if (isRecent) {
					recentFetchMore()
						.catch((error) => {
							setPaginationError(error);
						})
						.finally(() => {
							setPaginationLoading(false);
						});
				}
			}
		}
	}, [
		searchFetchMore,
		recentFetchMore,
		data?.pageInfo,
		isRecent,
		isSearch,
		loading,
		setPaginationError,
	]);

	const error = searchError || recentError || paginationError || spaceRootError;

	return {
		data,
		fetchMore,
		loading,
		error,
		refetch,
	};
};

type QueryData = useSpaceViewsDataContentQuery$data | ActivityQueryData | MyVisitsData | undefined;

type PageInfo = {
	hasNextPage: boolean;
	endCursor: string | null;
};

type SpaceViewsData = {
	pagesList: SpaceViewsPage[];
	pageInfo: PageInfo;
};

export const getNormalizedData = (data: QueryData): SpaceViewsData | undefined => {
	if (!data) {
		return data;
	}
	if (isSearchData(data)) {
		return normalizeSearch(data);
	}
	if (isLocalActivitiesData(data)) {
		return normalizeActivities(data);
	}
};

const isSearchData = (data: QueryData): data is useSpaceViewsDataContentQuery$data =>
	Boolean((data as useSpaceViewsDataContentQuery$data).content);

const isLocalActivitiesData = (data: QueryData): data is MyVisitsData =>
	Boolean((data as MyVisitsData).edges);

function isNotNull<T>(data: T | null): data is T {
	return data != null;
}

// Folders (especially without a view) don't make a whole lot of sense in the context of space views; as they are
// inherently a part of the tree view without a dedicated view of their own. So we filter them out.
function isNotFolder(data: useSpaceViewsDataContentQuery_content_nodes): boolean {
	return data?.type != null && data?.type !== 'folder';
}

export const normalizeSearch = (data: useSpaceViewsDataContentQuery$data): SpaceViewsData => {
	const pagesList = (data.content?.nodes || [])
		.filter(isNotNull)
		.filter(isNotFolder)
		.map((content) => {
			const { id, hasRestrictions, hasInheritedRestrictions } = content || {};
			const emojiPublished = getEmojiPublishedProperty(content);
			const emojiDraft = getEmojiDraftProperty(content);
			const { text: title, emoji: displayEmoji } = getTitleAndEmoji(
				content?.title || '',
				emojiPublished?.value,
			);
			return {
				title,
				id,
				hasRestrictions,
				hasInheritedRestrictions,
				displayEmoji,
				emojiPublished,
				emojiDraft,
				type: content?.type ?? undefined,
				subType: content?.subType ?? undefined,
			};
		});
	const pageInfo: PageInfo = {
		hasNextPage: Boolean(data.content?.pageInfo?.hasNextPage),
		endCursor: data.content?.pageInfo?.endCursor || null,
	};
	return {
		pagesList,
		pageInfo,
	};
};

const normalizeActivities = (data: MyVisitsData): SpaceViewsData => {
	const edges = data?.edges || [];
	const pagesList = edges.map((edge) => {
		const object = edge?.node?.object;
		const content = object?.content;
		const emojiPublished = getEmojiPublishedProperty(content);
		const emojiDraft = getEmojiDraftProperty(content);
		const { text: title, emoji: displayEmoji } = getTitleAndEmoji(
			content?.title || '',
			emojiPublished?.value,
		);

		return {
			title,
			id: object?.localResourceId || null,
			displayEmoji,
			emojiPublished,
			emojiDraft,
		};
	});
	const pageInfo: PageInfo = {
		hasNextPage: Boolean(data?.hasNextPage),
		endCursor: edges[edges.length - 1]?.cursor || null,
	};
	return {
		pagesList,
		pageInfo,
	};
};
